import { Add as AddIcon } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import { EntityGrid } from '@sophocles/data-grids';

// odl_app resource path for the firmware-upgrades grid (list + grid-spec).
const RESOURCE_PATH = 'firmware-upgrades';

const FirmwareUpgradeGridView = props => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Render-only overrides on top of the descriptor's columns: firmware version
  // string, requested-by full name, and the unresolved-ICCID count.
  const fieldOverrides = useMemo(
    () => ({
      firmware_version: {
        valueGetter: (_value, row) => `v${row.firmware_version?.version || ''}`,
      },
      requested_by: {
        valueGetter: (_value, row) => {
          const u = row.requested_by;
          if (!u) return '—';
          return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `#${u.id}`;
        },
      },
      unresolved_iccids: {
        valueGetter: value => (Array.isArray(value) ? value.length : 0),
      },
    }),
    [],
  );

  const handleRowClick = useCallback(({ row }) => navigate(`/staff/firmware-upgrades/${row.id}`), [navigate]);

  return (
    <AppNav page="admin_firmware_upgrades" notFullHeight flex {...props}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <TitleBar
          title={t('firmware.firmwareUpgrades', 'Firmware Upgrades')}
          actions={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/staff/firmware-upgrades/new')}
            >
              {t('firmware.newBulkUpgrade', 'New bulk upgrade')}
            </Button>
          }
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            px: 'var(--page-px)',
            pb: 'var(--page-py)',
          }}
        >
          <EntityGrid
            basePath={RESOURCE_PATH}
            fieldOverrides={fieldOverrides}
            options={{
              onRowClick: handleRowClick,
              showSearch: false,
              infiniteScroll: true,
              persistKey: 'firmwareUpgradesList',
              countNoun: t('firmware.batches', 'batches'),
            }}
          />
        </Box>
      </Box>
    </AppNav>
  );
};

export default FirmwareUpgradeGridView;
